import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  UserPlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { usePatientList, PatientFilter } from '../hooks/usePatientList';
import { useAuth } from '../hooks/useAuth';
import { getDoctors, assignDoctor, updatePatientStatus, generateDoctorLink } from '../services/api/apis';
import type { Patient, Doctor } from '../types';

interface PatientsPageProps {
  filter?: PatientFilter;
}

const STATUS_STYLES: Record<Patient['status'], string> = {
  pending:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  assigned: 'bg-blue-100   text-blue-800   dark:bg-blue-900/30   dark:text-blue-300',
  refunded: 'bg-green-100  text-green-800  dark:bg-green-900/30  dark:text-green-300',
  rejected: 'bg-red-100    text-red-800    dark:bg-red-900/30    dark:text-red-300',
  issued:   'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  submitted: 'bg-gray-100   text-gray-800   dark:bg-gray-900/30   dark:text-gray-300',
};

const STATUS_OPTIONS: Patient['status'][] = ['pending', 'assigned', 'refunded', 'rejected', 'issued', 'submitted'];

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const TABS: { label: string; filter: PatientFilter; path: string }[] = [
  { label: 'All Patients',  filter: 'all',       path: '/patients' },
  { label: 'Pending',       filter: 'pending',   path: '/patients/pending' },
  { label: 'Refunded',      filter: 'refunded',  path: '/patients/refunded' },
  { label: 'Submitted',     filter: 'submitted', path: '/patients/submitted' },
  { label: 'Assigned',      filter: 'assigned',  path: '/patients/assigned' },
  { label: 'Issued',        filter: 'issued',    path: '/patients/issued' },
];

const ADMIN_HEADERS = [
  'Patient',
  'Age / Gender',
  'Certificate Type',
  'Status',
  'Assign Doctor',
  'Date',
  'Change Status',
  'Generate Link',
  'Actions',
];

const READ_ONLY_HEADERS = [
  'Patient',
  'Age / Gender',
  'Certificate Type',
  'Status',
  'Date',
];

const SkeletonRow: React.FC<{ cols: number }> = ({ cols }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
      </td>
    ))}
  </tr>
);

export const PatientsPage: React.FC<PatientsPageProps> = ({ filter = 'pending' }) => {
  const {
    pageItems,
    pageSize,
    pageIndex,
    isLoading,
    error,
    hasMore,
    canGoPrev,
    goNext,
    goPrev,
    changePageSize,
    refresh,
  } = usePatientList(filter);

  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.groups?.includes('admin') ?? false;
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [copiedDocId, setCopiedDocId] = useState<string | null>(null);
  const [whatsappUrls, setWhatsappUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    getDoctors().then((docs) => {
      setDoctors(docs);
    }).catch(() => {});
  }, []);

  const setLoading = (key: string, val: boolean) =>
    setActionLoading((prev) => ({ ...prev, [key]: val }));

  const handleAssignDoctor = async (docId: string, doctorId: string) => {
    setLoading(docId, true);
    try {
      await assignDoctor(docId, doctorId);
      refresh();
    } catch (err) {
      console.error('Failed to assign doctor:', err);
    } finally {
      setLoading(docId, false);
    }
  };

  const handleChangeStatus = async (docId: string, status: string) => {
    setLoading(docId + '_status', true);
    try {
      await updatePatientStatus(docId, status as any);
      refresh();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setLoading(docId + '_status', false);
    }
  };

  const handleGenerateLink = async (docId: string, doctorId: string) => {
    if (!doctorId) return;
    setLoading(docId + '_link', true);
    try {
      const { link, whatsapp_url } = await generateDoctorLink(doctorId);
      await navigator.clipboard.writeText(link);
      setCopiedDocId(docId);
      setWhatsappUrls((prev) => ({ ...prev, [docId]: whatsapp_url }));
      setTimeout(() => setCopiedDocId((prev) => (prev === docId ? null : prev)), 2000);
    } catch (err) {
      console.error('Failed to generate link:', err);
    } finally {
      setLoading(docId + '_link', false);
    }
  };

  const headers = isAdmin ? ADMIN_HEADERS : READ_ONLY_HEADERS;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Patients</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isLoading ? 'Loading…' : `${pageItems.length} record${pageItems.length !== 1 ? 's' : ''} on this page`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            disabled={isLoading}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate('/patients/new')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <UserPlusIcon className="w-4 h-4" />
              New Patient
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {TABS.map((tab) => (
          <button
            key={tab.filter}
            onClick={() => navigate(tab.path)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              filter === tab.filter
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={refresh} className="ml-3 underline font-medium">Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {headers.map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={headers.length} />)
              ) : pageItems.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} className="px-6 py-16 text-center">
                    <p className="text-gray-400 dark:text-gray-600 font-medium">No patients found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-600 mt-1">
                      {filter === 'pending' ? 'No pending cases at the moment.' : 'Nothing here yet.'}
                    </p>
                  </td>
                </tr>
              ) : (
                pageItems.map((patient) => {
                  const rowId = patient.doc_id || patient.id || '';
                  const isAssignLoading = actionLoading[rowId];
                  const isStatusLoading = actionLoading[rowId + '_status'];
                  const isLinkLoading = actionLoading[rowId + '_link'];

                  return (
                    <tr
                      key={rowId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      {/* Patient */}
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {[patient.name, patient.last_name].filter(Boolean).join(' ') || patient.full_name || '—'}
                        </div>
                        {patient.doc_id && (
                          <div className="text-xs text-gray-400 mt-0.5">#{patient.doc_id}</div>
                        )}
                      </td>

                      {/* Age / Gender */}
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {patient.age || '—'} / {patient.gender || '—'}
                      </td>

                      {/* Certificate Type */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-700 dark:text-gray-200">
                          {patient.certificate_type || '—'}
                        </span>
                      </td>

                      {/* Status badge */}
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[patient.status]}`}
                        >
                          {patient.status}
                        </span>
                      </td>

                      {/* Assign Doctor — admin only */}
                      {isAdmin && (
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1.5">
                            <select
                              value={patient.assigned_doctor_id || ''}
                              disabled={isAssignLoading}
                              onChange={(e) => handleAssignDoctor(rowId, e.target.value)}
                              className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50 min-w-[130px]"
                            >
                              <option value="">— Unassigned —</option>
                              {doctors.map((d) => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                              ))}
                            </select>
                            {isAssignLoading && (
                              <span className="text-xs text-gray-400 animate-pulse">Saving…</span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Date */}
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(patient.created_at)}
                      </td>

                      {/* Change Status — admin only */}
                      {isAdmin && (
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1.5">
                            <select
                              value={patient.status}
                              disabled={isStatusLoading}
                              onChange={(e) => handleChangeStatus(rowId, e.target.value)}
                              className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s} className="capitalize">{s}</option>
                              ))}
                            </select>
                            {isStatusLoading && (
                              <span className="text-xs text-gray-400 animate-pulse">Saving…</span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Generate Link — admin only */}
                      {isAdmin && (
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleGenerateLink(rowId, patient.assigned_doctor_id || '')}
                              disabled={isLinkLoading || !patient.assigned_doctor_id}
                              title={!patient.assigned_doctor_id ? 'Assign a doctor first' : 'Generate secure doctor link'}
                              className="flex items-center gap-1.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {isLinkLoading ? (
                                <span className="text-xs text-gray-400 animate-pulse">Generating…</span>
                              ) : copiedDocId === rowId ? (
                                <>
                                  <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                                  <span className="text-green-600 dark:text-green-400">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <ClipboardDocumentIcon className="w-3.5 h-3.5 text-blue-500" />
                                  <span className="text-blue-600 dark:text-blue-400 hover:underline">Copy Link</span>
                                </>
                              )}
                            </button>
                            {whatsappUrls[rowId] && (
                              <a
                                href={whatsappUrls[rowId]}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Send via WhatsApp"
                                className="text-xs font-medium text-green-600 dark:text-green-400 hover:underline"
                              >
                                WA
                              </a>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Actions — admin only */}
                      {isAdmin && (
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => navigate(`/patients/${patient.doc_id}`)}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => navigate(`/patients/${patient.doc_id}/pdf`)}
                              className="text-sm text-purple-600 dark:text-purple-400 hover:underline font-medium"
                            >
                              PDF
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {!isLoading && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <span>Page {pageIndex}</span>
              <span>·</span>
              <span>{pageItems.length} on this page</span>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
                disabled={isLoading}
                className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <div className="flex items-center gap-1">
                <button
                  onClick={goPrev}
                  disabled={!canGoPrev || isLoading}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={goNext}
                  disabled={!hasMore || isLoading}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
